'use client'
import { createContext, useContext, useState, ReactNode } from 'react';
import DefaultDialog, { IDialogMessage } from 'components/Dialog/DefaultDialog';

interface DialogContextProps {
  openDialog: (dialogProps: Omit<IDialogMessage, 'id' | 'handleClose'>) => string;
  closeDialog: (id: string) => void;
  dialogs: IDialogMessage[];
}

const DialogContext = createContext<DialogContextProps | undefined>(undefined);

export function useDialog() {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
}

export default function DialogProvider({ children }: { children: ReactNode }) {
  const [dialogs, setDialogs] = useState<IDialogMessage[]>([]);

  const openDialog = (dialogProps: Omit<IDialogMessage, 'id' | 'handleClose'>) => {
    const id = crypto.randomUUID();
    
    const dialogMessage: IDialogMessage = {
      ...dialogProps,
      id,
      handleClose: (message) => closeDialog(message.id || id),
    };

    setDialogs((prev) => [...prev, dialogMessage]);
    return id;
  };

  const closeDialog = (id: string) => {
    setDialogs((prev) => prev.filter((dialog) => dialog.id !== id));
  };

  return (
    <DialogContext.Provider value={{ openDialog, closeDialog, dialogs }}>
      {children}
      {dialogs.map((dialog) => (
        <DefaultDialog key={dialog.id} {...dialog} />
      ))}
    </DialogContext.Provider>
  );
}